'use client';

/**
 * Reusable month-grid calendar.
 *
 * Two callsites today:
 * 1. /schedule full page — wrapped by FullSchedule, which renders the legend
 *    above the grid and shows a day-detail popover on click.
 * 2. Inbox map↔calendar toggle — used standalone with `showLegend=true` so
 *    operators can filter and rename crews from inside the inbox.
 *
 * Self-contained data fetch for the controlled `anchor` month. Crew display
 * name + color overrides come from per-tenant localStorage.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import {
  getCrewOverrides, getCrewLabel, getCrewColor as resolveCrewColor,
  type CrewOverride,
} from '@/lib/crew-display';
import { CrewLegend } from './CrewLegend';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';

export interface MonthVisit {
  id: string;
  customerId: string | null;
  customerName: string;
  serviceType: string;
  crew: string | null;
  startTime: string | null;
  status: string;
  allDay: boolean;
}

interface MonthResponse {
  visitCounts: Record<string, number>;
  visitsByCrew: Record<string, Record<string, number>>;
  visitsByDay: Record<string, MonthVisit[]>;
  crews: string[];
  monthStart: string;
  monthEnd: string;
}

const CREW_PALETTE = [
  '#00C2A8', '#F59E0B', '#EF4444', '#3B82F6', '#A855F7',
  '#10B981', '#EC4899', '#F97316', '#14B8A6', '#6366F1',
];

function pad(n: number): string { return String(n).padStart(2, '0'); }
function toDateStr(d: Date): string { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function fromDateStr(s: string): Date { return new Date(s + 'T12:00:00'); }
function todayStr(): string { return toDateStr(new Date()); }

function getMonthGrid(year: number, month: number): { date: Date; dateStr: string; inMonth: boolean }[] {
  const first = new Date(year, month, 1);
  // US-style Sun-first calendar. first.getDay() returns 0=Sun, so the offset
  // from Sunday is itself.
  const startOffset = first.getDay();
  const gridStart = new Date(year, month, 1 - startOffset);
  const cells: { date: Date; dateStr: string; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({ date: d, dateStr: toDateStr(d), inMonth: d.getMonth() === month });
  }
  return cells;
}

function formatMonthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Per-tenant overrides hook with cross-tab/cross-component sync.
function useCrewDisplay(tenantId: string | null) {
  const [overrides, setOverrides] = useState<Record<string, CrewOverride>>(() => getCrewOverrides(tenantId));

  useEffect(() => {
    setOverrides(getCrewOverrides(tenantId));
    if (typeof window === 'undefined') return;
    const handler = () => setOverrides(getCrewOverrides(tenantId));
    window.addEventListener('crewsync:crew-display-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('crewsync:crew-display-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, [tenantId]);

  return overrides;
}

export interface MiniMonthCalendarProps {
  tenantId: string | null;
  anchor: string;
  onPickDay: (dateStr: string) => void;
  onMonthChange?: (anchor: string) => void;
  showHeader?: boolean;
  showLegend?: boolean;
  legendEditable?: boolean;
  showNames?: boolean;
  crewFilter?: string | null;
  onCrewFilterChange?: (crew: string | null) => void;
  compact?: boolean;
  highlightToday?: boolean;
  selectedDateStr?: string | null;
  /** Fires whenever the month payload's crew list changes. Lets a parent
   *  build the same palette so day-detail panels (rendered outside this
   *  component) match the colors inside the grid. */
  onCrewListChange?: (crews: string[]) => void;
  /** Click on a customer-name chip in chip-stack mode. Opens the customer
   *  profile popover in the parent. Receives the same shape FullSchedule
   *  uses (CustomerSummary) so the parent can dedupe across surfaces. */
  onCustomerClick?: (visit: MonthVisit, crewLabel: string) => void;
}

export default function MiniMonthCalendar({
  tenantId, anchor, onPickDay, onMonthChange,
  showHeader = true, showLegend = true, legendEditable = true,
  showNames = false, crewFilter = null, onCrewFilterChange,
  compact = false, highlightToday = true, selectedDateStr = null,
  onCrewListChange, onCustomerClick,
}: MiniMonthCalendarProps) {
  const [data, setData] = useState<MonthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const overrides = useCrewDisplay(tenantId);

  const a = fromDateStr(anchor);
  const year = a.getFullYear();
  const month = a.getMonth();

  useEffect(() => {
    let cancelled = false;
    if (!tenantId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const url = `${API_URL}/api/schedule/month?year=${year}&month=${month + 1}${showNames ? '&details=1' : ''}`;
    authFetch(url)
      .then(r => r.ok ? r.json() : null)
      .then((res: MonthResponse | null) => {
        if (!cancelled && res) {
          setData(res);
          onCrewListChange?.(res.crews || []);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, year, month, showNames, onCrewListChange]);

  const palette = useMemo(() => buildCrewPalette(data?.crews || []), [data?.crews]);
  const colorFor = useCallback((crewKey: string) => resolveCrewColor(overrides, palette, crewKey), [overrides, palette]);
  const labelFor = useCallback((crewKey: string) => getCrewLabel(overrides, crewKey), [overrides]);

  const grid = getMonthGrid(year, month);
  const today = todayStr();
  const visitCounts = data?.visitCounts || {};
  const visitsByCrew = data?.visitsByCrew || {};
  const visitsByDay = data?.visitsByDay || {};

  const cellMinHeight = compact ? 44 : (showNames ? 110 : 64);
  const maxChipsPerCell = compact ? 0 : 5;

  const matchesFilter = (visit: { crew: string | null }) => {
    if (!crewFilter) return true;
    return (visit.crew || 'Unassigned') === crewFilter;
  };

  return (
    <div className="rounded-xl border border-[--border-primary] bg-white dark:bg-[#1a1a1a] overflow-hidden">
      {showHeader && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[--border-primary]">
          <div className="flex items-center gap-2 min-w-0">
            <Calendar className="w-4 h-4 text-[#00C2A8] shrink-0" />
            <h2 className="text-sm font-semibold text-[--text-primary] truncate">{formatMonthLabel(year, month)}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onMonthChange?.(toDateStr(new Date(year, month - 1, 1)))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-[#222] text-[--text-secondary] transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onMonthChange?.(todayStr())}
              className="px-2.5 py-1 text-[11px] font-semibold rounded-md text-[--text-secondary] hover:bg-gray-100 dark:hover:bg-[#222] transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => onMonthChange?.(toDateStr(new Date(year, month + 1, 1)))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-[#222] text-[--text-secondary] transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showLegend && (
        <CrewLegend
          tenantId={tenantId}
          crews={data?.crews || []}
          overrides={overrides}
          paletteFallback={palette}
          crewFilter={crewFilter}
          onCrewFilterChange={(c) => onCrewFilterChange?.(c)}
          editable={legendEditable}
        />
      )}

      <div className="p-3">
        <div className="grid grid-cols-7 gap-px mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1" aria-busy={loading}>
          {grid.map(cell => {
            const totalCount = visitCounts[cell.dateStr] || 0;
            const perCrew = visitsByCrew[cell.dateStr] || {};
            const filteredCount = crewFilter ? (perCrew[crewFilter] || 0) : totalCount;
            const isToday = highlightToday && cell.dateStr === today;
            const isSelected = cell.dateStr === selectedDateStr;
            const dayVisits = (visitsByDay[cell.dateStr] || []).filter(matchesFilter);
            const crewEntries = Object.entries(perCrew)
              .filter(([crew]) => !crewFilter || crew === crewFilter)
              .sort((a, b) => b[1] - a[1]);

            return (
              <button
                key={cell.dateStr}
                onClick={() => onPickDay(cell.dateStr)}
                style={{ minHeight: cellMinHeight }}
                className={`relative flex flex-col items-stretch py-1.5 px-1.5 rounded-lg text-[11px] transition-colors text-left ${
                  cell.inMonth
                    ? 'text-[--text-primary] hover:bg-gray-100 dark:hover:bg-[#222]'
                    : 'text-gray-300 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-[#1d1d1d]'
                } ${isToday ? 'ring-1 ring-[#00C2A8]' : ''} ${isSelected ? 'bg-[#00C2A8]/10 ring-1 ring-[#00C2A8]' : ''}`}
              >
                <span className={`font-medium ${isToday ? 'text-[#00C2A8]' : ''}`}>{cell.date.getDate()}</span>

                {showNames && filteredCount > 0 && (
                  <div className="mt-1 flex flex-col gap-0.5">
                    {dayVisits.slice(0, maxChipsPerCell).map((v, i) => {
                      const crewKey = v.crew || 'Unassigned';
                      const color = colorFor(crewKey);
                      const clickable = !!onCustomerClick;
                      const chipProps = {
                        key: v.id || i,
                        className: `rounded px-1 py-0.5 text-[9px] leading-tight truncate text-left ${clickable ? 'hover:opacity-80 cursor-pointer' : ''}`,
                        style: { backgroundColor: `${color}1A`, borderLeft: `2px solid ${color}` },
                        title: `${v.customerName} · ${v.serviceType} · ${labelFor(crewKey)}`,
                      };
                      const inner = <span className="font-semibold">{v.customerName}</span>;
                      return clickable ? (
                        <button
                          {...chipProps}
                          onClick={(e) => {
                            // Prevent the parent cell's onPickDay from firing.
                            e.stopPropagation();
                            onCustomerClick?.(v, labelFor(crewKey));
                          }}
                        >
                          {inner}
                        </button>
                      ) : (
                        <div {...chipProps}>{inner}</div>
                      );
                    })}
                    {dayVisits.length > maxChipsPerCell && (
                      <span className="px-1 text-[9px] font-semibold text-[--text-secondary]">
                        +{dayVisits.length - maxChipsPerCell} more
                      </span>
                    )}
                  </div>
                )}

                {!showNames && filteredCount > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {crewEntries.slice(0, 3).map(([crew, count]) => (
                      <span
                        key={crew}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold"
                        style={{ color: colorFor(crew) }}
                        title={`${labelFor(crew)}: ${count} visit${count !== 1 ? 's' : ''}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorFor(crew) }} />
                        {count}
                      </span>
                    ))}
                    {crewEntries.length > 3 && (
                      <span className="text-[9px] font-semibold text-[--text-tertiary]">
                        +{crewEntries.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {loading && (
          <div className="text-center text-[11px] text-[--text-tertiary] mt-2">Loading…</div>
        )}
      </div>
    </div>
  );
}

/** Public helper for callers that need to mirror the same color resolution
 *  (e.g. FullSchedule's day/week views render in the same palette). */
export function buildCrewPalette(crews: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  crews.forEach((c, i) => { map[c] = CREW_PALETTE[i % CREW_PALETTE.length]; });
  return map;
}
